import { useCustom, useGetLocale, useTranslate } from '@refinedev/core';
import {
  Alert,
  App as AntdApp,
  Button,
  Card,
  Input,
  List,
  Radio,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { format, parseISO } from 'date-fns';
import { dateFnsLocaleFor } from '../../utils/dateFormat';
import { useState } from 'react';
import { usePathwayCommand } from '../pathways/usePathwayCommand';

/**
 * E34 · CourseReviewQueue + ReviewPathwayPublication — "Backoffice — Course review" (cc-homework, chapter
 * "Pathways — Access, capabilities & course review (backoffice, E34)", screen e34e0012-0001-4000-8000-000000000020):
 * CopingCard reviews practices' public courses (Q-e34-18 A) — every version a practice with the publish
 * capability saved as public waits here: the practice, the course, its version and steps, a preview. Approve
 * makes that version public in Courses; Reject sends the reason back to the practice. CopingCard's own library
 * courses never pass through here.
 *
 * Behavior (the board): gesture Approve / Reject (with a reason) → ReviewPathwayPublication, i18n
 * backoffice.courses.review.*; empty-state nothing waiting, i18n backoffice.courses.review.empty; format a new
 * version of an approved course is marked "update" — Courses keeps offering the approved version until this one
 * is decided, i18n backoffice.courses.review.update. A refusal is shown verbatim against the decision.
 */
type Step = { stepId: string; kind: string; title: string };
type Section = { section: string; steps: Step[] };
type CourseReview = {
  pathwayId: string;
  version: number;
  practiceId: string;
  practiceName: string | null;
  name: string;
  description: string | null;
  sections: Section[];
  stepsCount: number;
  occurredAt: string;
  update: boolean;
  approvedVersion: number | null;
};

const REVIEW_FIELDS = [
  'pathwayId',
  'version',
  'practiceId',
  'practiceName',
  'name',
  'description',
  { sections: ['section', { steps: ['stepId', 'kind', 'title'] }] },
  'stepsCount',
  'occurredAt',
  'update',
  'approvedVersion',
];

const keyOf = (r: Pick<CourseReview, 'pathwayId' | 'version'>) => `${r.pathwayId}@${r.version}`;

export const BackofficeCourseReview = () => {
  const t = useTranslate();
  const getLocale = useGetLocale();
  const { message } = AntdApp.useApp();
  const { run, isLoading: saving } = usePathwayCommand();
  const { data, isFetching, isError, refetch } = useCustom<CourseReview[]>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: { operation: 'courseReviewQueue', fields: REVIEW_FIELDS },
  });
  const rows = data?.data ?? [];
  const [selected, setSelected] = useState<CourseReview | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [decision, setDecision] = useState<'approve' | 'reject'>('approve');
  const [reason, setReason] = useState('');
  const [failure, setFailure] = useState<string | null>(null);

  const choose = (r: CourseReview) => {
    setSelected(r);
    setDecision('approve');
    setReason('');
    setFailure(null);
  };

  const decide = async () => {
    if (!selected) return;
    setFailure(null);
    const result = await run(
      'reviewPathwayPublication',
      {
        pathwayId: selected.pathwayId,
        version: selected.version,
        decision,
        reason: decision === 'reject' ? reason.trim() || null : null,
      },
      t('backoffice.courses.review.failed'),
    );
    if (!result.ok) {
      setFailure(result.message);
      return;
    }
    message.success(
      t(decision === 'approve' ? 'backoffice.courses.review.approved' : 'backoffice.courses.review.rejected'),
    );
    setSelected(null);
    await refetch();
  };

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }} data-testid="backoffice-course-review">
      <div>
        <Typography.Title level={3} style={{ marginBottom: 0 }}>
          {t('backoffice.courses.review.title')}
        </Typography.Title>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
          {t('backoffice.courses.review.intro')}
        </Typography.Paragraph>
      </div>
      {isError ? <Alert type="error" showIcon message={t('backoffice.courses.review.loadFailed')} /> : null}
      <Table<CourseReview>
        rowKey={keyOf}
        loading={isFetching}
        dataSource={rows}
        pagination={{ pageSize: 25, hideOnSinglePage: true }}
        locale={{ emptyText: <span data-testid="backoffice-course-review-empty">{t('backoffice.courses.review.empty')}</span> }}
        onRow={(r) => ({ 'data-testid': `backoffice-review-row-${r.pathwayId}` } as never)}
        expandable={{
          expandedRowKeys: preview ? [preview] : [],
          showExpandColumn: false,
          expandedRowRender: (r) => (
            <List
              size="small"
              data-testid={`backoffice-review-preview-${r.pathwayId}`}
              dataSource={r.sections}
              renderItem={(s) => (
                <List.Item>
                  <Space direction="vertical" size={0}>
                    <Typography.Text strong>{s.section}</Typography.Text>
                    {s.steps.map((st) => (
                      <Typography.Text key={st.stepId} type="secondary">
                        {t('backoffice.courses.review.stepLine', {
                          title: st.title,
                          kind: t(`homework.kind.${st.kind}`),
                        })}
                      </Typography.Text>
                    ))}
                  </Space>
                </List.Item>
              )}
            />
          ),
        }}
        columns={[
          {
            title: t('backoffice.courses.review.practice'),
            key: 'practice',
            render: (_: unknown, r) => r.practiceName ?? r.practiceId,
          },
          {
            title: t('backoffice.courses.review.course'),
            key: 'course',
            render: (_: unknown, r) => (
              <Space direction="vertical" size={0}>
                <Typography.Text strong>{r.name}</Typography.Text>
                {r.description ? <Typography.Text type="secondary">{r.description}</Typography.Text> : null}
              </Space>
            ),
          },
          {
            title: t('backoffice.courses.review.version'),
            key: 'version',
            render: (_: unknown, r) => (
              <Space size={4}>
                <span>v{r.version}</span>
                {r.update ? (
                  <Tag
                    color="blue"
                    title={t('backoffice.courses.review.updateHelp', { version: r.approvedVersion ?? '' })}
                    data-testid={`backoffice-review-update-${r.pathwayId}`}
                  >
                    {t('backoffice.courses.review.update')}
                  </Tag>
                ) : null}
              </Space>
            ),
          },
          { title: t('backoffice.courses.review.steps'), dataIndex: 'stepsCount' },
          {
            title: t('backoffice.courses.review.submitted'),
            dataIndex: 'occurredAt',
            render: (at: string) => format(parseISO(at), 'd MMM yyyy', { locale: dateFnsLocaleFor(getLocale()) }),
          },
          {
            title: '',
            key: 'actions',
            render: (_: unknown, r) => (
              <Space>
                <Button
                  onClick={() => setPreview(preview === keyOf(r) ? null : keyOf(r))}
                  data-testid={`backoffice-review-preview-toggle-${r.pathwayId}`}
                >
                  {t(preview === keyOf(r) ? 'backoffice.courses.review.hidePreview' : 'backoffice.courses.review.preview')}
                </Button>
                <Button type="primary" ghost onClick={() => choose(r)} data-testid={`backoffice-review-decide-${r.pathwayId}`}>
                  {t('backoffice.courses.review.decide')}
                </Button>
              </Space>
            ),
          },
        ]}
      />

      {selected ? (
        <Card
          data-testid="backoffice-review-decision"
          title={t('backoffice.courses.review.decisionTitle', { name: selected.name, version: selected.version })}
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            <Radio.Group
              value={decision}
              onChange={(e) => setDecision(e.target.value)}
              data-testid="backoffice-review-decision-choice"
            >
              <Radio value="approve" data-testid="backoffice-review-choose-approve">
                {t('backoffice.courses.review.approve')}
              </Radio>
              <Radio value="reject" data-testid="backoffice-review-choose-reject">
                {t('backoffice.courses.review.reject')}
              </Radio>
            </Radio.Group>
            {decision === 'reject' ? (
              <div>
                <Typography.Text>{t('backoffice.courses.review.reason')}</Typography.Text>
                <Input.TextArea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={t('backoffice.courses.review.reasonPlaceholder')}
                  maxLength={2000}
                  autoSize={{ minRows: 3, maxRows: 8 }}
                  data-testid="backoffice-review-reason"
                />
              </div>
            ) : null}
            {failure ? (
              <Alert type="error" showIcon message={failure} data-testid="backoffice-review-error" />
            ) : null}
            <Space>
              <Button onClick={() => setSelected(null)} data-testid="backoffice-review-cancel">
                {t('backoffice.courses.review.cancel')}
              </Button>
              <Button
                type="primary"
                danger={decision === 'reject'}
                loading={saving}
                onClick={decide}
                data-testid="backoffice-review-submit"
              >
                {t(decision === 'approve' ? 'backoffice.courses.review.submitApprove' : 'backoffice.courses.review.submitReject')}
              </Button>
            </Space>
          </Space>
        </Card>
      ) : null}
    </Space>
  );
};
